import { useRef, useState } from "react";

import { Attachment } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, Group, rem, Text, UnstyledButton } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { IconPhoto, IconTrash } from "@tabler/icons-react";

import { ACCEPTED_IMAGE_TYPES, IMAGE_FILE_TYPES } from "@/types/uploads";
import { joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";
import {
  createAttachmentWithHash,
  getAttachmentTitle,
  getBinaryIdFromAttachmentUrl,
} from "@/utils/uploads";

type QuestionnaireAttachmentFieldProps<T> = {
  formFieldName: string;
  label: string;
  form: import("@mantine/form").UseFormReturnType<T>;
  required?: boolean;
  error?: string;
  /** Empty-state label (e.g. "+ Add Photo"). From questionnaire extension questionnaire-field-placeholder. */
  placeholder?: string;
};

const DEFAULT_ADD_PHOTO_LABEL = "+ Add Photo";

export default function QuestionnaireAttachmentField<T>({
  formFieldName,
  label,
  form,
  required,
  error,
  placeholder,
}: QuestionnaireAttachmentFieldProps<T>) {
  const medplum = useMedplum();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);

  const value = form.getInputProps(formFieldName).value as Attachment | null | undefined;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!IMAGE_FILE_TYPES.includes(file.type as (typeof IMAGE_FILE_TYPES)[number])) {
      toast({
        title: "Invalid file type",
        message: "Only image files are allowed (e.g. JPEG, PNG).",
        type: "error",
      });
      event.target.value = "";
      return;
    }

    setIsUploading(true);
    try {
      const attachment = await createAttachmentWithHash(medplum, {
        data: file,
        contentType: file.type,
        filename: file.name,
      });
      if (attachment) {
        (form.setFieldValue as (path: string, value: Attachment | null) => void)(
          formFieldName,
          attachment,
        );
      }
    } catch {
      showSystemErrorNotification();
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const handleView = async () => {
    if (!value?.url) return;
    setIsViewing(true);
    const notificationId = toast({
      title: "Preparing file",
      message: "Please wait while we prepare the file for viewing",
      type: "info",
    });
    try {
      const binaryId = getBinaryIdFromAttachmentUrl(value.url);
      if (!binaryId) {
        window.open(value.url, "_blank");
        return;
      }
      const blob = await medplum.download(joinReferenceString("Binary", binaryId));
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank");
    } catch {
      showSystemErrorNotification();
    } finally {
      notifications.hide(notificationId);
      setIsViewing(false);
    }
  };

  const handleRemove = () => {
    (form.setFieldValue as (path: string, value: Attachment | null) => void)(formFieldName, null);
  };

  const displayName = value ? getAttachmentTitle(value) : "";
  const hasFile = !!value?.url;

  const baseCardStyle = {
    borderRadius: "var(--mantine-radius-sm)",
    padding: `${rem(12)} ${rem(16)}`,
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: rem(8),
  };

  const filledCardStyle = {
    ...baseCardStyle,
    border: "1px solid var(--mantine-color-gray-4)",
    backgroundColor: "var(--mantine-color-white)",
  };

  const emptyCardStyle = {
    ...baseCardStyle,
    border: "2px dashed var(--mantine-color-gray-4)",
    backgroundColor: "var(--mantine-color-gray-0)",
    cursor: isUploading ? "wait" : "pointer",
  };

  return (
    <Box>
      {hasFile ? (
        <Box style={filledCardStyle}>
          <Text size="sm" c="gray.6" component="span">
            {label}
            {required && " *"}
          </Text>
          <Box style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Group gap="sm" maw="100%" wrap="nowrap" align="center">
              <IconPhoto size={18} color="var(--mantine-color-gray-6)" />
              <UnstyledButton
                type="button"
                onClick={handleView}
                disabled={isViewing}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                  color: "var(--mantine-color-burgundy-9)",
                  fontSize: "var(--mantine-font-size-sm)",
                  // Long filenames break instead of pushing the remove button off a phone screen.
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {displayName}
              </UnstyledButton>
              <UnstyledButton
                type="button"
                onClick={handleRemove}
                aria-label="Remove attachment"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  lineHeight: 1,
                  padding: rem(2),
                }}
              >
                <Box h={16} w={16} component="span" style={{ flexShrink: 0 }}>
                  <IconTrash
                    size={16}
                    style={{ display: "block" }}
                    color="var(--mantine-color-red-6)"
                  />
                </Box>
              </UnstyledButton>
            </Group>
          </Box>
        </Box>
      ) : (
        <UnstyledButton
          type="button"
          disabled={isUploading}
          style={emptyCardStyle}
          onClick={() => inputRef.current?.click()}
        >
          <Text size="sm" c="gray.6" component="span">
            {label}
            {required && " *"}
          </Text>
          <Box style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Text size="sm" c="burgundy.9" td="underline" component="span">
              {isUploading ? "Uploading…" : (placeholder ?? DEFAULT_ADD_PHOTO_LABEL)}
            </Text>
          </Box>
        </UnstyledButton>
      )}
      <input
        type="file"
        aria-hidden
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept={ACCEPTED_IMAGE_TYPES}
      />
      {error && (
        <Text mt={4} c="red" size="sm">
          {error}
        </Text>
      )}
    </Box>
  );
}
